import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BLOG_POSTS, CATEGORIES } from "../data/blogData";
import SEO from "../components/SEO";

const Blog = () => {
  const [activeCategory, setActiveCategory] = useState("All");

  const filteredPosts = activeCategory === "All" 
    ? BLOG_POSTS 
    : BLOG_POSTS.filter(post => post.category === activeCategory);

  const featuredPost = BLOG_POSTS.find(post => post.featured);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <SEO
        title="Corporate Catering & Office Meal Insights"
        description="Practical insights on corporate catering, office meals, workplace pantry management and corporate events in Bangalore."
        canonicalUrl="https://theofficemealco.com/blog"
        ogType="website"
      />
      {/* Breadcrumb */}
      <div className="bg-[#F3EFE6] py-4 px-6 md:px-12 lg:px-20 border-b border-[#E8DDCB]">
        <div className="max-w-7xl mx-auto flex items-center text-sm text-[#64748B] font-sans">
          <Link to="/" className="hover:text-[#1E3A5F] transition-colors">Home</Link>
          <span className="mx-2 text-[#CBA365]">/</span>
          <span className="text-[#1E3A5F] font-medium">Blog</span>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto text-center">
          <span className="text-[#CBA365] text-xs tracking-[0.24em] uppercase font-medium mb-4 block font-sans">
            Insights & Stories
          </span>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#1E3A5F] mb-6">
            The Office Meal Co. Blog
          </h1>
          <p className="text-lg text-[#64748B] max-w-2xl mx-auto font-sans">
            Expert insights on corporate catering, office dining trends, and workplace wellness from Bangalore's leading catering house.
          </p>
        </div>
      </section>

      {/* Featured Post */}
      {featuredPost && (
        <section className="px-6 md:px-12 lg:px-20 pb-16">
          <div className="max-w-7xl mx-auto">
            <Link to={`/blog/${featuredPost.slug}`} className="group block">
              <div className="grid lg:grid-cols-2 gap-0 bg-white shadow-sm overflow-hidden">
                <div className="relative h-[300px] lg:h-[400px] overflow-hidden">
                  <img 
                    src={featuredPost.image} 
                    alt={featuredPost.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 bg-[#CBA365] text-[#1E3A5F] text-xs uppercase tracking-[0.22em] px-3 py-1.5 font-medium">
                    Featured
                  </div>
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <span className="text-[#CBA365] text-xs tracking-[0.24em] uppercase font-medium mb-3 block font-sans">
                    {featuredPost.category}
                  </span>
                  <h2 className="font-serif text-2xl md:text-3xl text-[#1E3A5F] mb-4 group-hover:text-[#CBA365] transition-colors">
                    {featuredPost.title}
                  </h2>
                  <p className="text-[#64748B] mb-6 font-sans leading-relaxed">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-[#64748B] font-sans">
                    <span>{featuredPost.date}</span>
                    <span className="w-1 h-1 bg-[#CBA365] rounded-full"></span>
                    <span>{featuredPost.readTime}</span>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Category Filter */}
      <section className="px-6 md:px-12 lg:px-20 pb-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map(category => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-5 py-2 text-sm font-medium transition-colors font-sans ${
                  activeCategory === category
                    ? 'bg-[#1E3A5F] text-[#FDFBF7]'
                    : 'bg-white text-[#1E3A5F] border border-[#E8DDCB] hover:border-[#1E3A5F]'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="px-6 md:px-12 lg:px-20 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.filter(post => !post.featured || activeCategory !== "All").map(post => (
              <article key={post.id} className="bg-white shadow-sm hover:shadow-md transition-shadow group">
                <Link to={`/blog/${post.slug}`} className="block">
                  <div className="relative h-[220px] overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-3 left-3 bg-[#FDFBF7] text-[#1E3A5F] text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 font-medium">
                      {post.category}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-xl text-[#1E3A5F] mb-3 group-hover:text-[#CBA365] transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-[#64748B] text-sm mb-4 font-sans line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-[#64748B] font-sans">
                      <span>{post.date}</span>
                      <span className="w-1 h-1 bg-[#CBA365] rounded-full"></span>
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 md:px-12 lg:px-20 bg-[#1E3A5F]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-3xl text-[#FDFBF7] mb-4">
            Want to stay updated?
          </h2>
          <p className="text-[#E8DDCB] mb-8 font-sans">
            Get the latest insights on corporate dining delivered to your inbox.
          </p>
          <Link 
            to="/#contact" 
            className="inline-flex items-center gap-2 bg-[#CBA365] text-[#1E3A5F] px-8 py-4 font-medium hover:bg-[#E8DDCB] transition-colors font-sans"
          >
            Subscribe to Our Newsletter
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Blog;
